import { Component, Input, OnInit, Output, EventEmitter } from '@angular/core';

import { BehaviorSubject, Subscription } from 'rxjs';
import { includes } from 'lodash';

import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { SocialSearchService } from '../../../Core/services/social-search.service';

import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import { FilterService } from '../../../../Common/Filter/services/abstract-filter.service';
import { MediaSubscriptionStatusEnum } from '@Monitor/Shared/enums/media-subscription-status.enum';
import { AgentWidgetComponent, AgentWidgetMode } from '../AgentWidget/agent-widget.component';

import { FullProfileDialogAnimationsEnum } from '../../../../Core/Dialogs/enums/full-profile-dialog-animations.enum';
import { OverlayAnimationStateEnum } from '../../../../Core/Dialogs/enums/overlay-animation.enum';
import { FullProfileAnimationModel } from '../../../../Core/Dialogs/models/full-profile-animation.model';
import { MediaSubscription } from '../../../../Monitor/Shared/models/media-subscription.model';
import { Theme } from '../../models/theme.model';
import { LocalizationService } from '@Core/Localization/services/localization.service';


@Component({
    selector: 'agpr-agent-filter',
    templateUrl: './agent-filter.component.html',
    styleUrls: ['./agent-filter.component.scss'],
    providers: [
        {
            provide: FilterService,
            useExisting: SocialSearchService
        }
    ],
    standalone: false
})
export class AgentFilterComponent implements OnInit {

    @Input() currentTheme: Theme;

    @Input() set agents(themeAgents: MediaSubscription[]) {
        this.themeAgents = themeAgents;
        this.value = {};

        const agentIds: number[] = [];
        this.themeAgents.forEach(agent => {
            // is used when coming from the reports page, we don't want all the agents to be default selected true
            if (this.agentIds.length) {
                this.value[agent.id] = includes(this.agentIds, agent.id);
            } else {
                this.value[agent.id] = true;
            }
            agentIds.push(agent.id);
            this.agentActiveness[agent.id] = this.shouldBeLabelledActive(agent);
        });

        // can clear the agent IDs.. it's only for the initial selection when coming from reports page
        this.agentIds = [];
    }

    @Output() enabledAgents = new EventEmitter<number[]>();
    @Output() createNewAgent = new EventEmitter<null>();

    value: { [key: number]: boolean } = {};
    themeAgents: MediaSubscription[] = [];
    selectAll: boolean = true;
    overlayContainer: Element;
    agentActiveness: { [key: number]: boolean } = {};

    maxReachedTooltip: string;

    agentIds: number[] = [];

    readonly id: string = 'agent_filter';
    readonly AGENT_MAX = Theme.MAX_NUM_AGENTS;
    readonly buttonTypes = ButtonTypesEnum;

    private subscriptions: Subscription[] = [];

    constructor(
        private socialSearchService: SocialSearchService,
        private coreDialogService: CoreDialogService,
        private localizationService: LocalizationService
    ) { }


    ngOnInit() {
        this.overlayContainer = document.getElementById('main-content');

        this.subscriptions.push(
            this.socialSearchService.agentIds$.subscribe(agentIds => {
                this.agentIds = agentIds;
            })
        );

        this.maxReachedTooltip = this.localizationService.transform('Social.intelligence.themeManagement.themeCard.addAgent.maxReached', {
            max: this.AGENT_MAX
        });
    }

    /**
     * Apply the current selected filters
     */
    private apply() {
        const agentIds = Object.entries(this.value)
            .filter(([key, value]) => value)
            .map(([key, value]) => +key);
        this.enabledAgents.emit(agentIds);
    }

    /**
     * focus select agent, deselect all other agents
     */
    isolate(agentId: number) {
        this.selectAll = false;
        Object.keys(this.value).forEach(x => {
            this.value[x] = false;
        });
        this.value[agentId] = true;
        this.apply();
    }

    update() {
        let allSet: boolean = true;
        Object.keys(this.value).forEach(x => {
            allSet = allSet && this.value[x];
        });

        this.selectAll = allSet;
        this.apply();
    }


    editAgent(agent: MediaSubscription) {
        const animationTrigger = new BehaviorSubject(
            new FullProfileAnimationModel(
                FullProfileDialogAnimationsEnum.slideInOutAnimation,
                OverlayAnimationStateEnum.open
            )
        );

        const io = {
            inputs: {
                animationTrigger: animationTrigger,
                overlayContainer: this.overlayContainer,
                theme: this.currentTheme,
                agentModel: agent,
                mode: AgentWidgetMode.edit
            },
            outputs: {}
        };
        this.coreDialogService.open(AgentWidgetComponent, io);
    }

    toggleSelectAll() {
        Object.keys(this.value).forEach(x => {
            this.value[x] = this.selectAll;
        });
        this.apply();
    }

    externalUpdate(agentIds: number[]) {
        Object.keys(this.value).forEach(x => {
            this.value[x] = agentIds.includes(+x);
        });
        this.update();
    }

    private shouldBeLabelledActive(agent: MediaSubscription): boolean {
        return agent.status === MediaSubscriptionStatusEnum.ACTIVE
            || agent.status === MediaSubscriptionStatusEnum.STAND_BY;
    }
}
